/**
 * RELATION PANEL GROUP — wrapper opzionale dei RelationPanel di una sidebar.
 *
 * Rende una piccola toolbar con un unico pulsante toggle "Comprimi/Espandi
 * tutti" e propaga un *segnale* ai RelationPanel figli via context. Il segnale
 * e' un contatore: i panel reagiscono al suo cambio (non al valore), allineando
 * il proprio stato expanded a `collapsed`. Tra un toggle e l'altro ogni panel
 * resta liberamente apribile/chiudibile dall'utente.
 *
 * Senza questo wrapper i RelationPanel restano del tutto autonomi (context
 * null): nessun cambio di comportamento.
 *
 * NB: la toolbar e' attualmente NASCOSTA (vedi `SHOW_TOGGLE_ALL`). Il meccanismo
 * sottostante resta intatto e funzionante: i panel continuano a essere
 * apribili/chiudibili singolarmente, e riattivare il comando e' questione di
 * cambiare una costante.
 */

import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import { RelationPanelGroupContext } from './relationPanelGroupContext';

/**
 * Mostra il pulsante "Comprimi/Espandi tutti". Disattivato su richiesta: il
 * comando non e' abbastanza usato da giustificare lo spazio in cima a una
 * colonna gia' densa. Il codice resta interamente al suo posto — rimettere
 * `true` lo fa riapparire, senza altre modifiche.
 */
const SHOW_TOGGLE_ALL: boolean = false;

export function RelationPanelGroup({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(false);
  const [signal, setSignal] = useState(0);

  function toggleAll() {
    // collapsed e signal cambiano insieme: i panel leggono il nuovo collapsed
    // quando l'effetto scatta sul cambio di signal.
    setCollapsed((c) => !c);
    setSignal((s) => s + 1);
  }

  const actionLabel = collapsed ? t('relationship.expandAll') : t('relationship.collapseAll');

  return (
    <RelationPanelGroupContext.Provider value={{ signal, collapsed }}>
      {SHOW_TOGGLE_ALL && (
        <Box className="relation-panel-group__toolbar">
          <Tooltip title={actionLabel}>
            <IconButton size="small" onClick={toggleAll} aria-label={actionLabel}>
              {collapsed ? <UnfoldMoreIcon fontSize="small" /> : <UnfoldLessIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>
      )}
      {children}
    </RelationPanelGroupContext.Provider>
  );
}
